import React, { useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  PiggyBank,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { BudgetForm } from '@/components/budget/BudgetForm';
import {
  useBudgets,
  useCreateBudget,
  useUpdateBudget,
  useDeleteBudget,
} from '@/hooks/useBudgets';
import { useTranslation } from '@/i18n';
import type { Budget, CreateBudgetInput } from '@/types/budget';
import { getBudgetStatus } from '@/types/budget';

function BudgetCard({
  budget,
  onEdit,
  onDelete,
  t,
  formatCurrency,
}: {
  budget: Budget;
  onEdit: () => void;
  onDelete: () => void;
  t: ReturnType<typeof useTranslation>['t'];
  formatCurrency: (value: number) => string;
}) {
  const percentage = Math.min((budget.spent / budget.amount) * 100, 100);
  const status = getBudgetStatus(budget.spent, budget.amount);
  const remaining = budget.amount - budget.spent;

  const statusColors = {
    safe: 'bg-green-500',
    warning: 'bg-yellow-500',
    danger: 'bg-red-500',
  };

  const statusBgColors = {
    safe: 'bg-green-100 dark:bg-green-900/30',
    warning: 'bg-yellow-100 dark:bg-yellow-900/30',
    danger: 'bg-red-100 dark:bg-red-900/30',
  };

  const statusTextColors = {
    safe: 'text-green-600 dark:text-green-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
    danger: 'text-red-600 dark:text-red-400',
  };

  const StatusIcon = status === 'safe' ? CheckCircle : status === 'warning' ? AlertTriangle : AlertCircle;

  return (
    <Card className="group hover:shadow-md transition-shadow">
      <CardContent>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusBgColors[status]}`}>
              <StatusIcon className={`w-5 h-5 ${statusTextColors[status]}`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {budget.name}
              </h3>
              {budget.categoryName && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {budget.categoryName}
                </p>
              )}
            </div>
          </div>
          <Badge variant={budget.period === 'monthly' ? 'primary' : 'secondary'}>
            {budget.period === 'monthly' ? t.app.budgets.period.monthly : t.app.budgets.period.yearly}
          </Badge>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600 dark:text-gray-400">
              {formatCurrency(budget.spent)} {t.app.budgets.spent}
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {percentage.toFixed(0)}%
            </span>
          </div>
          <div className="h-3 bg-gray-100 dark:bg-secondary-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${statusColors[status]}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="text-gray-500 dark:text-gray-400">
              {t.app.budgets.limit}: {formatCurrency(budget.amount)}
            </span>
            <span className={remaining >= 0 ? statusTextColors.safe : statusTextColors.danger}>
              {remaining >= 0 ? `${t.app.budgets.remaining} ${formatCurrency(remaining)}` : `${t.app.budgets.exceeded} ${formatCurrency(Math.abs(remaining))}`}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-secondary-700">
          {status === 'danger' && (
            <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {t.app.budgets.status.danger}
            </span>
          )}
          {status === 'warning' && (
            <span className="text-xs text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {t.app.budgets.status.warning}
            </span>
          )}
          {status === 'safe' && (
            <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {t.app.budgets.status.safe}
            </span>
          )}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="sm" onClick={onEdit} className="p-2">
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export const BudgetsPage: React.FC = () => {
  const { t, locale } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);
  const [deletingBudget, setDeletingBudget] = useState<Budget | null>(null);

  const { data, isLoading, isError } = useBudgets();
  const createMutation = useCreateBudget();
  const updateMutation = useUpdateBudget();
  const deleteMutation = useDeleteBudget();

  const budgets = data?.budgets || [];
  const summary = data?.summary;

  const formatCurrency = (value: number) => {
    const localeMap: Record<string, string> = {
      'pt-BR': 'pt-BR',
      'en-GB': 'en-GB',
      'es-ES': 'es-ES',
      'fr-FR': 'fr-FR',
      'it-IT': 'it-IT',
      'ar': 'ar-SA',
      'zh-CN': 'zh-CN',
    };
    const currencyMap: Record<string, string> = {
      'pt-BR': 'BRL',
      'en-GB': 'GBP',
      'es-ES': 'EUR',
      'fr-FR': 'EUR',
      'it-IT': 'EUR',
      'ar': 'SAR',
      'zh-CN': 'CNY',
    };
    return new Intl.NumberFormat(localeMap[locale] || 'en-GB', {
      style: 'currency',
      currency: currencyMap[locale] || 'GBP',
    }).format(value);
  };

  const handleCreate = () => {
    setEditingBudget(null);
    setIsFormOpen(true);
  };

  const handleEdit = (budget: Budget) => {
    setEditingBudget(budget);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (formData: CreateBudgetInput) => {
    if (editingBudget) {
      await updateMutation.mutateAsync({
        id: editingBudget.id,
        data: formData,
      });
    } else {
      await createMutation.mutateAsync(formData);
    }
    setIsFormOpen(false);
    setEditingBudget(null);
  };

  const handleDelete = async () => {
    if (deletingBudget) {
      await deleteMutation.mutateAsync(deletingBudget.id);
      setDeletingBudget(null);
    }
  };

  if (isLoading) {
    return <PageLoader message={t.app.budgets.loading} />;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t.app.budgets.error}
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          {t.app.budgets.errorDesc}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t.app.budgets.title}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t.app.budgets.subtitle}
          </p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={handleCreate}>
          {t.app.budgets.newBudget}
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && budgets.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <PiggyBank className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t.app.budgets.summary.totalBudgeted}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(summary.totalBudgeted)}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t.app.budgets.summary.totalSpent}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(summary.totalSpent)}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                summary.overBudgetCount > 0
                  ? 'bg-red-100 dark:bg-red-900/30'
                  : 'bg-green-100 dark:bg-green-900/30'
              }`}>
                {summary.overBudgetCount > 0 ? (
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t.app.budgets.summary.overBudget}</p>
                <p className={`text-xl font-bold ${
                  summary.overBudgetCount > 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400'
                }`}>
                  {summary.overBudgetCount} {summary.overBudgetCount === 1 ? t.app.budgets.summary.budget : t.app.budgets.summary.budgets}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Budgets List */}
      {budgets.length === 0 ? (
        <Card className="p-12 text-center">
          <PiggyBank className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t.app.budgets.noBudgets}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-6">
            {t.app.budgets.noBudgetsDesc}
          </p>
          <Button onClick={handleCreate} leftIcon={<Plus className="w-4 h-4" />}>
            {t.app.budgets.createFirst}
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {budgets.map((budget) => (
            <BudgetCard
              key={budget.id}
              budget={budget}
              onEdit={() => handleEdit(budget)}
              onDelete={() => setDeletingBudget(budget)}
              t={t}
              formatCurrency={formatCurrency}
            />
          ))}
        </div>
      )}

      {/* Tips Card */}
      <Card className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-800">
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <PiggyBank className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                {t.app.budgets.tips.title}
              </h3>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                <li>• {t.app.budgets.tips.tip1}</li>
                <li>• {t.app.budgets.tips.tip2}</li>
                <li>• {t.app.budgets.tips.tip3}</li>
                <li>• {t.app.budgets.tips.tip4}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <BudgetForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingBudget(null);
        }}
        onSubmit={handleFormSubmit}
        budget={editingBudget}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingBudget}
        onClose={() => setDeletingBudget(null)}
        title={t.app.budgets.confirmDelete}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {t.app.budgets.confirmDeleteMsg}{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {deletingBudget?.name}
            </span>
            ?
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setDeletingBudget(null)}>
              {t.app.common.cancel}
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={deleteMutation.isPending}
            >
              {t.app.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BudgetsPage;
